import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { Trash2 } from 'lucide-react';
import useNoteStore from '../../store/noteStore';
import useCalendarStore from '../../store/calendarStore';
import { EventType, EVENT_TYPE_CONFIGS } from '../../types/eventTypes';
import { RecurringConfig } from '../../types/recurringTypes';
import { Reminder, createDefaultReminders } from '../../types/reminderTypes';
import { getFestivalLunarDate } from '../../utils/festivalDates';
import calendarApi from '../../services/calendarApi';
import EventTypeSelector from './EventTypeSelector';
import EventDatePicker from './EventDatePicker';
import DynamicEventFields from './DynamicEventFields';
import RecurringToggle from './RecurringToggle';
import ReminderSelector from './ReminderSelector';
import { ConfirmModal } from '../Common';

const NoteEditor: React.FC = () => {
    const { currentDate } = useCalendarStore();
    const { isEditorOpen, editingNote, closeEditor, createNote, updateNote, deleteNote, fetchAllNotes } = useNoteStore();

    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [tags, setTags] = useState('');
    const [eventDate, setEventDate] = useState<Date>(currentDate);
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Event Type fields
    const [eventType, setEventType] = useState<EventType>(EventType.KHAC);
    const [eventData, setEventData] = useState<Record<string, unknown>>({});
    // Recurring config (Feature A2)
    const [recurring, setRecurring] = useState<RecurringConfig | undefined>(undefined);
    // Reminders (Feature A5)
    const [reminders, setReminders] = useState<Reminder[]>([]);

    useEffect(() => {
        if (editingNote) {
            setTitle(editingNote.title);
            setContent(editingNote.content);
            setTags(editingNote.tags?.join(', ') || '');
            setEventType(editingNote.eventType || EventType.KHAC);
            setEventData(editingNote.eventData || {});
            setRecurring(editingNote.recurring);
            // Restore event date
            if (editingNote.date) {
                setEventDate(parseISO(editingNote.date));
            }
            // Restore reminders
            setReminders(editingNote.reminders || []);
        } else {
            setTitle('');
            setContent('');
            setTags('');
            setEventDate(currentDate);
            setEventType(EventType.KHAC);
            setEventData({});
            setRecurring(undefined);
            setReminders([]);
        }
    }, [editingNote, isEditorOpen, currentDate]);

    const handleEventTypeChange = (type: EventType) => {
        setEventType(type);
        setEventData({}); // Reset data when type changes
        // Set default reminders for new event type
        const defaultDays = EVENT_TYPE_CONFIGS[type]?.defaultReminders || [];
        setReminders(createDefaultReminders(defaultDays));
    };

    // Handle event data changes - auto-select date for Lễ Tết festivals
    const handleEventDataChange = async (data: Record<string, unknown>) => {
        setEventData(data);

        // If this is a Lễ Tết and festivalName changed, auto-select the date
        if (eventType === EventType.LE_TET && data.festivalName) {
            const festivalName = data.festivalName as string;
            const lunarDate = getFestivalLunarDate(festivalName);

            if (lunarDate) {
                // Convert lunar date to solar date for current year
                const currentYear = new Date().getFullYear();
                const solarDate = await calendarApi.lunarToSolar(lunarDate.lunarDay, lunarDate.lunarMonth, currentYear);

                if (solarDate) {
                    setEventDate(parseISO(solarDate));
                }
            }
        }
    };

    const handleSave = async () => {
        // Allow save if: has title OR has content OR has eventData OR chose specific eventType
        const hasContent = title.trim() || content.trim() || Object.keys(eventData).length > 0;
        const hasSpecificEventType = eventType !== EventType.KHAC;

        if (!hasContent && !hasSpecificEventType) {
            closeEditor();
            return;
        }

        setIsSaving(true);
        const tagArray = tags.split(',').map((t) => t.trim()).filter(Boolean);
        const dateStr = format(eventDate, 'yyyy-MM-dd');

        try {
            // For Lễ Tết, use festivalName as title
            const finalTitle = eventType === EventType.LE_TET
                ? (eventData.festivalName as string || 'Lễ Tết')
                : title.trim();

            if (editingNote) {
                await updateNote(editingNote.id || editingNote.localId || '', {
                    date: dateStr,
                    title: finalTitle,
                    content: content.trim(),
                    tags: tagArray,
                    eventType,
                    eventData,
                    recurring,
                    reminders,
                });
            } else {
                await createNote({
                    date: dateStr,
                    title: finalTitle,
                    content: content.trim(),
                    tags: tagArray,
                    eventType,
                    eventData,
                    recurring,
                    reminders,
                });
            }
            closeEditor();
            // Refresh the notes list to show updated data
            await fetchAllNotes();
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!editingNote) return;
        setIsDeleting(true);
        try {
            await deleteNote(editingNote.id || editingNote.localId || '');
            setShowDeleteConfirm(false);
            closeEditor();
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <AnimatePresence>
            {isEditorOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeEditor}
                        className="fixed inset-0 bg-black/50 z-40"
                    />

                    {/* Editor Modal - Full screen on mobile, centered modal on desktop */}
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className={`fixed inset-0 md:inset-auto md:bottom-0 md:left-1/2 md:-translate-x-1/2 md:max-w-2xl md:w-full md:rounded-t-3xl z-50 flex flex-col max-h-[90vh] shadow-2xl ${EVENT_TYPE_CONFIGS[eventType]?.bgColor || 'bg-gray-50'
                            }`}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex-shrink-0 bg-white/50 backdrop-blur-sm">
                            <button
                                onClick={closeEditor}
                                className="text-gray-600 hover:text-gray-900 px-3 py-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-base sm:text-lg font-medium"
                            >
                                Hủy
                            </button>
                            <h3 className="text-gray-900 font-semibold text-base sm:text-lg">
                                {editingNote ? 'Sửa sự kiện' : 'Sự kiện mới'}
                            </h3>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="text-blue-600 hover:text-blue-700 px-3 py-2 min-h-[44px] min-w-[44px] flex items-center justify-center font-semibold disabled:opacity-50 text-base sm:text-lg"
                            >
                                {isSaving ? 'Đang lưu...' : 'Lưu'}
                            </button>
                        </div>

                        {/* Form - scrollable on mobile */}
                        <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-4">
                            {/* Event Type Selector */}
                            <EventTypeSelector
                                selected={eventType}
                                onSelect={handleEventTypeChange}
                            />

                            {/* Title - hide for LE_TET since festivalName is used as title */}
                            {eventType !== EventType.LE_TET && (
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Tiêu đề
                                    </label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="Nhập tiêu đề..."
                                        className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base shadow-sm"
                                        autoFocus
                                    />
                                </div>
                            )}

                            {/* Dynamic Fields Based on Event Type */}
                            <DynamicEventFields
                                eventType={eventType}
                                eventData={eventData}
                                onChange={handleEventDataChange}
                            />

                            {/* Event Date Picker */}
                            <EventDatePicker
                                value={eventDate}
                                onChange={setEventDate}
                                eventType={eventType}
                            />

                            {/* Recurring Toggle (Feature A2) */}
                            <RecurringToggle
                                config={recurring}
                                eventType={eventType}
                                onChange={setRecurring}
                            />

                            {/* Reminders (Feature A5) */}
                            <ReminderSelector
                                reminders={reminders}
                                onChange={setReminders}
                                eventType={eventType}
                                eventDate={eventDate}
                            />

                            {/* Additional Notes */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Ghi chú thêm
                                </label>
                                <textarea
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    placeholder="Thêm ghi chú..."
                                    rows={4}
                                    className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-base shadow-sm"
                                />
                            </div>

                            {/* Tags */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Tags
                                </label>
                                <input
                                    type="text"
                                    value={tags}
                                    onChange={(e) => setTags(e.target.value)}
                                    placeholder="Phân cách bằng dấu phẩy"
                                    className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base shadow-sm"
                                />
                            </div>

                            {/* Delete button - only for existing notes */}
                            {editingNote && (
                                <button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    className="w-full flex items-center justify-center gap-2 py-3 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                                >
                                    <Trash2 className="w-5 h-5" />
                                    <span>Xóa ghi chú này</span>
                                </button>
                            )}

                            <p className="text-gray-500 text-xs">
                                Ngày: {format(currentDate, 'dd/MM/yyyy')}
                            </p>
                        </div>
                    </motion.div>
                </>
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={handleDelete}
                title="Xóa ghi chú?"
                message="Bạn có chắc muốn xóa ghi chú này? Hành động này không thể hoàn tác."
                confirmText="Xóa"
                cancelText="Hủy"
                variant="danger"
                isLoading={isDeleting}
            />
        </AnimatePresence>
    );
};

export default NoteEditor;
